import React, { useEffect, useState } from 'react';
import { DetailsRow } from './DetailsRowContentElements/DetailsRow';
import { colors } from '@atlaskit/theme';
import { Dimensions } from 'components/visuals';
import { messages } from 'modules/mac/messages';
import { FormattedMessage } from 'react-intl';
import Tooltip from '../../../../components/common/Tooltip';
import { StyledParagraphWithTextAlign, StyledSpanWithLineHeight } from '../styledComponents';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import { styled } from 'styled-components';
import Button, { IconButton } from '@atlaskit/button/new';
import { StyledFlex, StyledTextLabel } from './StyledComponents';
import EditFilledIcon from '@atlaskit/icon/glyph/edit-filled';
import Textfield from '@atlaskit/textfield';
import {
  clearAppParentSenError,
  fetchAppParentSen,
  updateAppParentSen,
} from 'modules/mac/duck/actions/accounts';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorMessage } from '@atlaskit/form';
import { selectAppParentSen, selectUpdatedAppParentSen } from 'modules/mac/duck/selectors';
import Spinner from '@atlaskit/spinner';
import { State } from 'model/State';
import { getAppParentBySen, getAppParentBySlug } from 'modules/mac/duck/api';
import { isSen, isSlug } from 'modules/utils';

interface AppParentSENProps {
  appSen: string;
  appParentData?: { parentAccountId: string | null };
}

interface ParentApp {
  parentAccountId: number;
  parentAccountName: string;
  parentAccountSlug: string;
}

const TextfieldWrapper = styled.div`
  width: 80%;
`;

const AddParentFieldWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EditIconComponent = () => (
  <EditFilledIcon
    label="mac.orders.table.table-item.action.edit"
    size="small"
    testId="edit-parent-sen-icon"
  />
);

const AppCouplingComponent: React.FC<AppParentSENProps> = ({ appSen }) => {
  const dispatch = useDispatch();
  const appState = useSelector((state: State) => state);
  const appParentData = useSelector((state: State) => selectAppParentSen(state));
  const updatedAppParentData = useSelector((state: State) => selectUpdatedAppParentSen(state));
  const parentAccountId = appParentData?._result?.parentAccountId;

  const [isInEditMode, setIsInEditMode] = useState<boolean>(
    parentAccountId === null || parentAccountId === undefined,
  );
  const [inputParentSen, setInputParentSen] = useState<string>('');
  const [emptyError, setEmptyError] = useState<boolean>(false);
  const [appParentSenValue, setAppParentSenValue] = useState<any>(parentAccountId);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [parentApp, setParentApp] = useState<ParentApp | null>(null);

  useEffect(() => {
    setInputParentSen('');
    setEmptyError(false);
    setIsInEditMode(false);
  }, []);

  useEffect(() => {
    dispatch(clearAppParentSenError());
  }, [appSen]);

  useEffect(() => {
    setAppParentSenValue(parentAccountId);
  }, [appParentData]);

  useEffect(() => {
    if (updatedAppParentData?.error) {
      setValidationError(updatedAppParentData.error.i18nFieldErrors[0].defaultMessage);
    } else if (updatedAppParentData?._result) {
      setValidationError(null);
      setIsInEditMode(false);
      setAppParentSenValue(updatedAppParentData._result.parentAccountId);
      setInputParentSen('');
      setParentApp(null);
    } else {
      setValidationError(null);
    }
  }, [updatedAppParentData]);

  const getAppParent = async (id: string) => {
    try {
      if (isSen(id)) {
        const data = await getAppParentBySen(id.replace('SEN-', ''), appState);
        setParentApp(data as ParentApp);
      } else if (isSlug(id)) {
        const data = await getAppParentBySlug(id, appState);
        setParentApp(data as ParentApp);
      }
    } catch {
      setParentApp(null);
    }
  };

  return (
    <>
      {appParentData?.inProgress && <Spinner size="small" />}
      {parentAccountId && !isInEditMode && (
        <DetailsRow
          label={
            <>
              <FormattedMessage {...messages.installedOn} />
              <Tooltip
                direction="up-middle"
                content={
                  <StyledParagraphWithTextAlign $textAlign="center">
                    <FormattedMessage {...messages.appParentSENTooltip} />
                  </StyledParagraphWithTextAlign>
                }
              >
                <StyledSpanWithLineHeight $marginLeft={`${Dimensions.units(0.5)}px`}>
                  <QuestionCircleIcon size="small" primaryColor={colors.N80} label="sen-tooltip" />
                </StyledSpanWithLineHeight>
              </Tooltip>
            </>
          }
          value={
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>{appParentSenValue ? `SEN-${appParentSenValue}` : ''}</span>
              <IconButton
                onClick={() => {
                  setInputParentSen('');
                  setIsInEditMode(true);
                  setParentApp(null);
                }}
                appearance="subtle"
                spacing="compact"
                label="edit parent sen"
                icon={EditIconComponent}
              />
            </div>
          }
          dataCy="mac-parent-sen-details"
        />
      )}
      {(isInEditMode || parentAccountId === null) && (
        <>
          <StyledFlex data-cy="mac-add-parent-sen" style={{ alignItems: 'baseline' }} width="100%">
            <StyledTextLabel>
              <FormattedMessage {...messages.installedOn} />
              <Tooltip
                direction="up-middle"
                content={
                  <StyledParagraphWithTextAlign $textAlign="center">
                    <FormattedMessage {...messages.appParentSENTooltip} />
                  </StyledParagraphWithTextAlign>
                }
              >
                <StyledSpanWithLineHeight $marginLeft={`${Dimensions.units(0.5)}px`}>
                  <QuestionCircleIcon size="small" primaryColor={colors.N80} label="sen-tooltip" />
                </StyledSpanWithLineHeight>
              </Tooltip>
            </StyledTextLabel>
            <div>
              <AddParentFieldWrapper>
                <TextfieldWrapper>
                  <Textfield
                    style={{ height: '32px' }}
                    name="parentSen"
                    id="parentSen"
                    value={inputParentSen}
                    onChange={(e) => {
                      setEmptyError(false);
                      setValidationError(null);
                      setInputParentSen(e.target.value);
                      setParentApp(null);
                    }}
                    data-cy="mac-parent-sen-input"
                    placeholder="SEN-XXXXXXXX/E-XXX-XXX-XXX-XXX"
                    isInvalid={emptyError}
                  />
                </TextfieldWrapper>
                {!parentApp?.parentAccountName && (
                  <div style={{ width: '100px' }} data-cy="mac-search-parent-sen-button">
                    <Button
                      shouldFitContainer
                      appearance="primary"
                      onClick={() => {
                        if (!inputParentSen) {
                          setEmptyError(true);
                          return;
                        }
                        getAppParent(inputParentSen);
                      }}
                    >
                      <FormattedMessage {...messages.searchButton} />
                    </Button>
                  </div>
                )}
                {parentApp?.parentAccountName && (
                  <div style={{ width: '100px' }} data-cy="mac-link-parent-sen-button">
                    <Button
                      shouldFitContainer
                      appearance="primary"
                      onClick={() => {
                        if (!inputParentSen) {
                          setEmptyError(true);
                          return;
                        }
                        dispatch(
                          updateAppParentSen(
                            appSen,
                            String(parentApp?.parentAccountId),
                            parentApp?.parentAccountSlug,
                          ),
                        );
                        dispatch(fetchAppParentSen(appSen));
                      }}
                    >
                      <FormattedMessage {...messages.linkButton} />
                    </Button>
                  </div>
                )}
                <div style={{ width: '100px' }}>
                  <Button
                    shouldFitContainer
                    appearance="subtle"
                    onClick={() => {
                      setIsInEditMode(false);
                      setInputParentSen('');
                      setParentApp(null);
                      setEmptyError(false);
                      setValidationError(null);
                    }}
                  >
                    <FormattedMessage {...messages.addNewContactModalCancel} />
                  </Button>
                </div>
              </AddParentFieldWrapper>
              {parentApp?.parentAccountName && (
                <div style={{ marginTop: '8px' }}>{parentApp?.parentAccountName}</div>
              )}
              {emptyError && (
                <ErrorMessage>
                  <FormattedMessage
                    defaultMessage="This field is required. It can not be empty"
                    id="mac.payment-details.billing-form.field-empty"
                  />
                </ErrorMessage>
              )}
              {validationError && <ErrorMessage>{validationError}</ErrorMessage>}
            </div>
          </StyledFlex>
        </>
      )}
    </>
  );
};

export default AppCouplingComponent;
